import { useContext, useRef } from "react";
import { useSelector } from "react-redux";
import {
  collection,
  doc,
  setDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { useI18n } from "i18n";
import logger from "logging/logger";
import { useAppDispatch } from "store/hooks";
import { selectUserId } from "modules/auth/redux/selectors";
import { selectCurrentEventId } from "modules/event/selectors";
import { Errors, trackError } from "modules/monitoring";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { firestore } from "services/firebaseService/firebaseConfig";
import { startTranscribing } from "modules/transcription/redux";
import { stopTranscribing } from "modules/transcription/redux/transcriptionSlice";
import { TRANSCRIPTION_STATUS_COLLECTION } from "../../constants";

const getTranscriptionStatusEventDoc = (eventId: string) =>
  doc(collection(firestore, TRANSCRIPTION_STATUS_COLLECTION), eventId);

export const useTranscriptionRequests = () => {
  const { t } = useI18n(["event"]);
  const dispatch = useAppDispatch();
  const { track } = useContext(TRACKING_CONTEXT);
  const currentEventId = useSelector(selectCurrentEventId);
  const userId = useSelector(selectUserId);
  const { addErrorNotification } = useNotificationActions();

  const requestListener = useRef<{ listener: null | (() => void) }>({
    listener: null,
  });

  const turnOnTranscriptionForEvent = async () => {
    if (!currentEventId || !userId) {
      return;
    }

    try {
      const docRef = getTranscriptionStatusEventDoc(currentEventId);

      await setDoc(docRef, {
        status: "active",
        timestamp: serverTimestamp(),
      });

      track(Events.TRANSCRIPTION_STATUS_TOGGLED, {
        eventId: currentEventId,
        userId,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : error;

      addErrorNotification({
        message: t("event:transcription.request.error"),
      });
      logger.error(
        `[Transcription] An error occurred while toggling transcription: ${errorMessage}`,
        {
          eventId: currentEventId,
        },
      );
      trackError(error, {
        label: Errors.TRANSCRIPTION_STATUS_TOGGLED,
      });
    }
  };

  const subscribe = () => {
    if (!currentEventId) {
      return;
    }

    requestListener.current.listener = onSnapshot(
      getTranscriptionStatusEventDoc(currentEventId),
      (doc) => {
        if (doc.data()?.status === "active") {
          dispatch(startTranscribing());
        }
      },
    );
  };

  const unsubscribe = () => {
    dispatch(stopTranscribing());
    if (requestListener.current.listener) {
      requestListener.current.listener();
    }
  };

  return {
    turnOnTranscriptionForEvent,
    subscribe,
    unsubscribe,
  };
};
